import type { AuthArgs } from '../../api/authArgs.ts';
import {
  getObservabilityStatus,
  observabilityErrorMessage,
  ObservabilityError,
  sendObservabilityQuestion,
  type ObservabilityMessage,
  type ObservabilityResponse,
  type ObservabilityStatus,
} from '../../api/observabilityClient.ts';

export interface ObservabilityState {
  status: ObservabilityStatus | null;
  loading: boolean;
  pending: boolean;
  targetId: string;
  modelId: string;
  lookbackHours: number;
  question: string;
  history: ObservabilityMessage[];
  result: { question: string; response: ObservabilityResponse } | null;
  error: string | null;
  notice: string | null;
}

export function lookbackOptions(maximum: number): number[] {
  return [...new Set([1, 6, 24, maximum].filter((value) => value <= maximum))].sort((a, b) => a - b);
}

export function canAskObservability(state: ObservabilityState): boolean {
  return !!state.status?.enabled && state.status.configured
    && !state.loading && !state.pending
    && state.status.targets.some((target) => target.id === state.targetId)
    && state.status.models.some((model) => model.id === state.modelId)
    && Number.isInteger(state.lookbackHours)
    && state.lookbackHours >= 1 && state.lookbackHours <= state.status.maxLookbackHours
    && state.question.trim().length > 0
    && state.question.length <= state.status.maxQuestionLength;
}

// Owned by one mounted page; nothing is persisted or shared with customer chat.
export function createObservabilitySession(getAuthArgs: () => Promise<AuthArgs | null>) {
  let state: ObservabilityState = {
    status: null, loading: true, pending: false, targetId: '', modelId: '', lookbackHours: 1,
    question: '', history: [], result: null, error: null, notice: null,
  };
  const listeners = new Set<() => void>();
  let statusRequest: AbortController | null = null;
  let chatRequest: AbortController | null = null;

  function update(changes: Partial<ObservabilityState>) {
    state = { ...state, ...changes };
    listeners.forEach((listener) => listener());
  }

  function abortChat() {
    chatRequest?.abort();
    chatRequest = null;
  }

  async function load(availabilityError: string | null = null) {
    statusRequest?.abort();
    abortChat();
    const controller = new AbortController();
    statusRequest = controller;
    update({ loading: true, pending: false, status: null, history: [], result: null, error: availabilityError, notice: null });
    try {
      const args = await getAuthArgs();
      if (controller.signal.aborted) return;
      if (!args) throw new ObservabilityError(401);
      const status = await getObservabilityStatus(args, controller.signal);
      if (controller.signal.aborted) return;
      const retainedModel = status.models.some((model) => model.id === state.modelId);
      const modelId = retainedModel ? state.modelId : status.defaultModelId ?? '';
      const modelRemoved = !!state.modelId && !retainedModel;
      update({
        status,
        modelId,
        targetId: status.targets.some((target) => target.id === state.targetId)
          ? state.targetId : status.targets[0]?.id ?? '',
        lookbackHours: Math.min(24, status.maxLookbackHours),
        notice: modelRemoved
          ? `The selected chat model is no longer available. Conversation history was cleared.${modelId ? ' The advertised default is now selected.' : ' Select an available chat model to continue.'}`
          : null,
      });
    } catch (error) {
      if (!controller.signal.aborted) update({ error: observabilityErrorMessage(error) });
    } finally {
      if (statusRequest === controller) {
        statusRequest = null;
        update({ loading: false });
      }
    }
  }

  function changeScope(targetId: string, lookbackHours: number) {
    if (!state.status
      || !state.status.targets.some((target) => target.id === targetId)
      || !Number.isInteger(lookbackHours) || lookbackHours < 1 || lookbackHours > state.status.maxLookbackHours
      || (targetId === state.targetId && lookbackHours === state.lookbackHours)) return;
    statusRequest?.abort();
    statusRequest = null;
    abortChat();
    update({
      targetId, lookbackHours, history: [], result: null, error: null, pending: false,
      notice: 'Scope changed. Conversation history was cleared.',
    });
  }

  function changeModel(modelId: string) {
    if (!state.status?.models.some((model) => model.id === modelId) || modelId === state.modelId) return;
    abortChat();
    update({
      modelId, history: [], result: null, error: null, pending: false,
      notice: 'Chat model changed. Conversation history was cleared.',
    });
  }

  async function send() {
    if (!canAskObservability(state) || !state.status) return;
    const controller = new AbortController();
    chatRequest = controller;
    const { targetId, modelId, lookbackHours, status } = state;
    const question = state.question.trim();
    const history = state.history.map(({ role, content }) => ({ role, content }));
    update({ pending: true, error: null, notice: null });
    try {
      const args = await getAuthArgs();
      if (controller.signal.aborted) return;
      if (!args) throw new ObservabilityError(401);
      const response = await sendObservabilityQuestion({ targetId, modelId, question, lookbackHours, history }, args, controller.signal);
      if (controller.signal.aborted) return;
      if (response.targetId !== targetId || response.modelId !== modelId || response.lookbackHours !== lookbackHours) {
        throw new ObservabilityError(502);
      }
      const completed: ObservabilityMessage[] = [
        ...history, { role: 'user', content: question },
        { role: 'assistant', content: response.answer.slice(0, status.maxQuestionLength) },
      ];
      update({
        history: status.maxHistoryMessages === 0 ? [] : completed.slice(-status.maxHistoryMessages),
        result: { question, response },
        question: '',
      });
    } catch (error) {
      if (!controller.signal.aborted) {
        if (error instanceof ObservabilityError && error.status === 409) await load(error.message);
        else update({ error: observabilityErrorMessage(error) });
      }
    } finally {
      if (chatRequest === controller) {
        chatRequest = null;
        update({ pending: false });
      }
    }
  }

  return {
    getSnapshot: () => state,
    subscribe(listener: () => void) {
      listeners.add(listener);
      return () => { listeners.delete(listener); };
    },
    load: () => load(),
    send,
    changeScope,
    changeModel,
    setQuestion(question: string) {
      if (!state.pending) update({ question });
    },
    cancel() {
      if (!chatRequest) return;
      abortChat();
      update({ pending: false, notice: 'Request canceled. Your question has been kept.' });
    },
    clear() {
      abortChat();
      update({ pending: false, history: [], result: null, error: null, notice: 'Conversation history was cleared.' });
    },
    dispose() {
      statusRequest?.abort();
      statusRequest = null;
      abortChat();
    },
  };
}
